import TableComponent, { HeadCell } from '../../component/table/table.component';
import { ConditionType, SearchKeyInput, SearchKeyInputItems } from '../../component/form/search-key-input.component';
import i18n from '../../i18n';
import { useEffect, useState } from 'react';
import apiManage from '../../request';
import { Restaurant } from '../../models';
import ButtonComponent from '../../component/button/button.component';
import { CheckCircle } from '@mui/icons-material';
import _ from 'lodash';
import ISVRestaurantDetailDialog from './isv-fee-setting';
import { Box, Button } from '@mui/material';
import DialogComponent from '../../component/dialog/dialog.component';
import GlobalNotification from '../../component/common/snackbar';

export default function ISVRestaurantDialog(props: Readonly<{
    // 弹窗开关
    isOpen: boolean;
    // 关闭弹窗
    handleClose: () => void;
    // 确定
    handleConfirm: () => void;
}>) {
    // 表格数据
    const [list, setList] = useState<Restaurant[]>([])
    const [totalCount, setTotalCount] = useState(0)
    // 表格loading
    const [loading, setLoading] = useState(false)
    // 页码
    const [pagPage, setPagPage] = useState(1)
    const [pagPerPage, setPagPerPage] = useState(20)
    // 排序
    const [sortKey, setSortKey] = useState('')
    const [sortValue, setSortValue] = useState<0 | 1 | 2>(0)
    // 搜索
    const [conditions, setConditions] = useState<ConditionType[]>([]);

    // 当前选中项
    const [selectedData, setSelectedData] = useState<Restaurant[]>([])
    // 打开新增弹窗
    const [isOpenDetail, setIsOpenDetail] = useState(false)

    useEffect(() => {
        // 排序、分页改变时重新获取数据
        getData()
    }, [sortKey, sortValue, pagPage, pagPerPage, conditions])


    /**
     * 请求餐馆数据
     */
    const getData = async () => {
        setLoading(true)
        // 搜索条件
        // eslint-disable-next-line prefer-const
        let searchObj = {}
        conditions.forEach((item) => {
            searchObj[item.key] = searchObj[item.key] ? _.concat(searchObj[item.key], item.value) : [item.value];
        })
        const result = await apiManage.get_related_isv_restaurants({
            join_isv: false,
            pag_per_page: pagPerPage,
            pag_page: pagPage,
            sort_key: (sortKey === '' || sortValue === 0) ? undefined : sortKey,
            sort_value: sortValue === 0 ? undefined : (sortValue === 1 ? -1 : 1),
            ...searchObj
        })
        // 请求成功
        if (result && !result.error) {
            setList(result?.[0]?.dbResults ?? [])
            setTotalCount(result?.[0]?.totalCount?.[0]?.count ?? 0)
        }
        setLoading(false)
    }

    /**
     * 页码回调
     * @param params
     */
    const changePageCallback = (params: {
        page: number;
        curRowsPerPage: number;
    }) => {
        setPagPerPage(params.curRowsPerPage)
        setPagPage(params.page + 1)
    }

    /**
     * 排序
     * @param headCell
     */
    const handleSort = (headCell: HeadCell) => {
        let newSortValue = sortValue;
        switch (headCell.sortStatus) {
            case 0: {
                newSortValue = 1;
                break;
            }
            case 1: {
                newSortValue = 2;
                break;
            }
            case 2: {
                newSortValue = 0;
                break;
            }
        }
        setPagPage(1)
        setSortKey(headCell.id)
        setSortValue(newSortValue)
    }

    /**
     * 搜索
     * @param conditions
     */
    const handleSearch = (conditions: ConditionType[]) => {
        setPagPage(1)
        setConditions([...conditions])
    }

    /**
     * 选择/取消选择
     * @param value
     * @param selectedIndex
     */
    const handleSelected = (value: Restaurant, selectedIndex: number) => {
        const data = selectedData
        if (selectedIndex > -1) {
            data.splice(selectedIndex, 1)
        } else {
            data.push({
                isv_fee: 0.2,
                nickname: value.nickname,
                string_id: value.string_id
            })
        }
        setSelectedData([...data])
    }


    /**
     * 打开弹窗
     */
    const handleOpen = () => {
        if (selectedData.length > 0) {
            setIsOpenDetail(true)
        } else {
            GlobalNotification.error(`${i18n.t('google_text_please_select')}`)
        }
    }

    /**
     * 关闭弹窗
     */
    const handleClose = () => {
        setIsOpenDetail(false)
    }

    /**
     * 点击确定
     * 关闭弹窗
     * 刷新页面
     */
    const handleConfirm = () => {
        setIsOpenDetail(false)
        setSelectedData([])
        getData()
        props.handleConfirm()
    }


    // 查找表格条件下拉框 静态数据
    const filterList: SearchKeyInputItems[] = [
        {
            value: 'nickname',
            label: i18n.t('operation_record_restaurant_nickname'),
            type: 'TEXT'
        }
    ];


    // 表格头部
    const tableHeadCell: HeadCell[] = [
        {
            id: 'nickname',
            label: i18n.t('restaurant_management_page_table_text_restaurant_name'),
            sortStatus: sortKey === 'nickname' ? sortValue : 0
        },
        {
            id: 'string_id',
            label: i18n.t('restaurant_page_input_text_restaurant_identification_code'),
            sortStatus: sortKey === 'string_id' ? sortValue : 0
        },
        {
            id: 'belonged_group',
            label: i18n.t('general_text_belonged_group'),
            sortStatus: sortKey === 'belonged_group' ? sortValue : 0,
            renderFunction: (row) => {
                return row?.belonged_group?.[0]?.nickname ?? ''
            }
        },
        {
            id: 'reg_city',
            sortStatus: sortKey === 'reg_city' ? sortValue : 0,
            label: i18n.t('restaurant_management_page_table_text_city'),
        },
        {
            id: 'operate',
            label: i18n.t('general_text_operating'),
            renderFunction: (row: Restaurant) => {
                const selectedIndex = selectedData.findIndex((item) => item.string_id === row.string_id)
                return (
                    <ButtonComponent iconBtn={false} handleClick={() => handleSelected(row, selectedIndex)} >
                        {
                            selectedIndex > -1 ?
                                <div className='FC'>
                                    <CheckCircle />
                                    <span>{i18n.t('google_text_selected')}</span>
                                </div>
                                :
                                i18n.t('google_text_select')
                        }
                    </ButtonComponent>
                )
            }
        }
    ]

    return (
        <DialogComponent
            open={props.isOpen}
            maxWidth='md'
            heightSize='large'
            defaultHeaderProps={{
                title: 'ISV',
            }}
            onClose={props.handleClose}
            footer={(
                <>
                    <Button
                        color='primary'
                        variant='outlined'
                        onClick={props.handleClose}
                    >{i18n.t('google_text_public_button_cancel')}</Button>
                    <Box marginLeft={4} display='inline-block'>
                        <ButtonComponent
                            color='primary'
                            variant='contained'
                            async
                            handleClick={handleOpen}
                        >
                            {i18n.t('google_text_confirm')}
                        </ButtonComponent>
                    </Box>

                </>

            )} >
            <SearchKeyInput
                items={filterList}
                conditions={conditions}
                onSearch={handleSearch}
                showConditionsBox
            />
            <TableComponent
                showChecked={0}
                list={list}
                headCellList={tableHeadCell}
                isTableLoading={loading}
                isPagination={true}
                allCount={totalCount}
                handleSort={handleSort}
                changePageCallback={changePageCallback}
                isCurrentPageData={true}
                currentPage={pagPage - 1}
            />
            <ISVRestaurantDetailDialog
                actionMode='ADD'
                detail={selectedData}
                isOpen={isOpenDetail}
                handleClose={handleClose}
                handleConfirm={handleConfirm}
            />
        </DialogComponent>
    )
}
